import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterChips from "@/components/base/FilterChips";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/base/Pagination";
import {
  applyFilters,
  collectGenres,
  collectRegions,
  collectYears,
  paginate,
} from "@/lib/media";
import type { MediaItem } from "@/types/media";

interface CategoryListPageProps {
  title: string;
  description: string;
  breadcrumbLabel: string;
  list: MediaItem[];
  type: "movie" | "tv";
}

const PAGE_SIZE = 6;

const sortOptions = [
  { value: "latest", label: "按上映时间" },
  { value: "hot", label: "按热门程度" },
  { value: "rating", label: "按评分" },
] as const;

export default function CategoryListPage({
  title,
  description,
  breadcrumbLabel,
  list,
  type,
}: CategoryListPageProps) {
  usePageMeta({
    title: `${title}-555电影网`,
    description: description.slice(0, 150),
    keywords: `${title},555电影网,影视推荐,影视筛选`,
  });

  const [genres, setGenres] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [episodes, setEpisodes] = useState<string[]>([]);
  const [sort, setSort] = useState<"latest" | "hot" | "rating">("latest");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const genreOptions = useMemo(
    () => collectGenres(list).map((g) => ({ value: g, label: g })),
    [list],
  );
  const yearOptions = useMemo(
    () => collectYears(list).map((y) => ({ value: String(y), label: `${y}年` })),
    [list],
  );
  const regionOptions = useMemo(
    () => collectRegions(list).map((r) => ({ value: r, label: r })),
    [list],
  );
  const episodeOptions = [
    { value: "short", label: "20集以内" },
    { value: "medium", label: "21-40集" },
    { value: "long", label: "40集以上" },
  ];

  const filtered = useMemo(
    () => applyFilters(list, { genres, years, region: regions, episodes, sort }),
    [list, genres, years, regions, episodes, sort],
  );

  const paged = paginate(filtered, page, PAGE_SIZE);

  const toggle = (
    value: string,
    current: string[],
    setter: (next: string[]) => void,
  ) => {
    setter(current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
    setPage(1);
  };

  const reset = () => {
    setGenres([]);
    setYears([]);
    setRegions([]);
    setEpisodes([]);
    setSort("latest");
    setPage(1);
  };

  const activeCount = genres.length + years.length + regions.length + episodes.length;

  const filterPanel = (
    <div className="divide-y divide-background-200">
      <FilterChips
        label="类型"
        options={genreOptions}
        selected={genres}
        onToggle={(v) => toggle(v, genres, setGenres)}
      />
      <FilterChips
        label="年份"
        options={yearOptions}
        selected={years}
        onToggle={(v) => toggle(v, years, setYears)}
      />
      <FilterChips
        label="地区"
        options={regionOptions}
        selected={regions}
        onToggle={(v) => toggle(v, regions, setRegions)}
      />
      {type === "tv" && (
        <FilterChips
          label="集数"
          options={episodeOptions}
          selected={episodes}
          onToggle={(v) => toggle(v, episodes, setEpisodes)}
        />
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-page px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: breadcrumbLabel }]} />

      <div className="mt-4 rounded-lg border border-background-200 bg-background-100 px-5 py-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
          {title}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          {description}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">共 {filtered.length} 部</span>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSort(option.value);
                    setPage(1);
                  }}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                    sort === option.value
                      ? "bg-background-50 text-primary-700 shadow-sm"
                      : "text-foreground-500 hover:text-secondary-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setMobileFilterOpen((prev) => !prev)}
            className="mt-4 flex w-full items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line"></i>
              筛选{activeCount > 0 ? `（${activeCount}）` : ""}
            </span>
            <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>

          {mobileFilterOpen && (
            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 px-4 py-1 lg:hidden">
              {filterPanel}
            </div>
          )}

          {paged.items.length === 0 ? (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
              <div className="flex h-16 w-16 items-center justify-center text-foreground-300">
                <i className="ri-emotion-sad-line text-4xl"></i>
              </div>
              <p className="mt-4 text-sm text-foreground-600">没有找到符合条件的影视作品</p>
              <button
                type="button"
                onClick={reset}
                className="mt-4 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                {paged.items.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination
                currentPage={paged.currentPage}
                totalPages={paged.totalPages}
                onChange={setPage}
              />
            </>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-2">
            <h2 className="border-b border-background-200 py-3 font-heading text-sm font-semibold text-foreground-900">
              筛选条件
            </h2>
            {filterPanel}
          </div>
        </aside>
      </div>
    </div>
  );
}